import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { FiCalendar } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import { blogPosts } from "../../data/blogPosts";
import { getBlogPosts } from "../../api/blog";
import { resolveMediaUrl } from "../../utils/media";
import leftArrow from "../../assets/icons/left.svg";
import rightArrow from "../../assets/icons/right.svg";
import copyIcon from "../../assets/icons/copy.svg";
import linkedinIcon from "../../assets/icons/linkedin.svg";
import twitterIcon from "../../assets/icons/twitter.svg";
import facebookIcon from "../../assets/icons/facebook.svg";
import "./BlogDetails.css";

const buildLinkedInShareUrl = (pageUrl) =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;

const buildFacebookShareUrl = (pageUrl) =>
  `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;

const buildXShareUrl = (pageUrl, title) => {
  const params = new URLSearchParams();
  params.set("url", pageUrl);
  if (title) params.set("text", title);
  return `https://twitter.com/intent/tweet?${params.toString()}`;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fallbackDetailsPosts = blogPosts.map((post) => ({
  id: String(post.id),
  title: post.title || "",
  desc: post.desc || "",
  text: post.desc || "",
  date: post.date || "",
  image: post.image || "",
  caption: post.caption || "",
  sections: undefined,
}));

const BULLET_LINE =
  /^\s*(?:[-*•‣⁃]|\u2022)\s*(.+)$/;
const ORDERED_LINE = /^\s*\d{1,3}[.)]\s*(.+)$/;

const parseBlogBody = (raw) => {
  const lines = String(raw ?? "").split(/\r?\n/);
  const segments = [];
  let i = 0;

  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (!trimmed) {
      i += 1;
      continue;
    }

    if (BULLET_LINE.test(trimmed)) {
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (!t) break;
        const m = t.match(BULLET_LINE);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      segments.push({ type: "ul", items });
      continue;
    }

    if (ORDERED_LINE.test(trimmed)) {
      const items = [];
      while (i < lines.length) {
        const t = lines[i].trim();
        if (!t) break;
        const m = t.match(ORDERED_LINE);
        if (!m) break;
        items.push(m[1]);
        i += 1;
      }
      segments.push({ type: "ol", items });
      continue;
    }

    const block = [];
    while (i < lines.length) {
      const t = lines[i].trim();
      if (!t) break;
      if (BULLET_LINE.test(t) || ORDERED_LINE.test(t)) break;
      block.push(lines[i]);
      i += 1;
    }
    const text = block.join("\n").trim();
    if (text) segments.push({ type: "p", text });
  }

  return segments;
};

const mergeBlogBodyParagraphs = (segments) => {
  if (!segments.length) return segments;
  const firstListIdx = segments.findIndex((s) => s.type === "ul" || s.type === "ol");

  if (firstListIdx > 0) {
    const head = segments.slice(0, firstListIdx);
    if (head.length >= 2 && head.every((s) => s.type === "p")) {
      const mergedText = head.map((s) => s.text).join("\n");
      return [{ type: "p", text: mergedText }, ...segments.slice(firstListIdx)];
    }
    return segments;
  }

  let j = 0;
  while (
    j < segments.length &&
    segments[j].type === "p" &&
    !segments[j].text.includes("\n")
  ) {
    j += 1;
  }
  if (j < 2) return segments;
  const mergedText = segments.slice(0, j).map((s) => s.text).join("\n");
  return [{ type: "p", text: mergedText }, ...segments.slice(j)];
};

const pickFirstDefined = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
};

/** Map API sections whether keys are camelCase, PascalCase, or snake_case. */
const normalizeBlogSection = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const sectionType = pickFirstDefined(
    raw.sectionType,
    raw.SectionType,
    raw.section_type,
    raw.type,
    raw.Type
  );
  const titleRaw = pickFirstDefined(raw.title, raw.Title);
  const bodyRaw = pickFirstDefined(raw.body, raw.Body);
  const bulletsRaw = pickFirstDefined(raw.bullets, raw.Bullets, raw.bulletPoints, raw.BulletPoints);
  const id = pickFirstDefined(raw.id, raw.Id);
  const displayOrder = pickFirstDefined(raw.displayOrder, raw.DisplayOrder, raw.display_order);

  const title = titleRaw == null ? "" : String(titleRaw).trim();
  const body = bodyRaw == null ? "" : String(bodyRaw).trim();
  const bullets = Array.isArray(bulletsRaw)
    ? bulletsRaw
        .map((x) => (typeof x === "string" ? x : x?.text != null ? String(x.text) : String(x ?? "")))
        .map((t) => t.trim())
        .filter(Boolean)
    : [];

  return {
    id,
    displayOrder,
    sectionType,
    title,
    body,
    bullets,
  };
};

/** @param {unknown} sections */
const buildBlocksFromSections = (sections) => {
  if (!Array.isArray(sections) || sections.length === 0) return null;

  const sorted = [...sections].sort((a, b) => {
    const na = normalizeBlogSection(a);
    const nb = normalizeBlogSection(b);
    const ao = Number(na?.displayOrder);
    const bo = Number(nb?.displayOrder);
    if (Number.isFinite(ao) && Number.isFinite(bo)) return ao - bo;
    if (Number.isFinite(ao)) return -1;
    if (Number.isFinite(bo)) return 1;
    return 0;
  });

  const blocks = [];
  for (const raw of sorted) {
    const s = normalizeBlogSection(raw);
    if (!s) continue;

    const sid = s.id != null ? String(s.id) : `s-${blocks.length}`;
    const st = String(s.sectionType ?? "")
      .toLowerCase()
      .trim();
    const { title, body, bullets } = s;

    if (st === "heading" || st === "h2" || st === "subtitle") {
      const headingText = title || body;
      if (headingText) blocks.push({ kind: "h2", id: sid, text: headingText });
      continue;
    }

    if (st === "paragraph" || st === "p" || st === "text" || st === "") {
      if (body && bullets.length) {
        blocks.push({ kind: "richParagraph", id: sid, body, bullets });
      } else if (body) {
        blocks.push({ kind: "p", id: `${sid}-p`, text: body });
      } else if (bullets.length) {
        blocks.push({ kind: "richParagraph", id: sid, body: "", bullets });
      }
      continue;
    }

    if (st === "list" || st === "bullets") {
      if (bullets.length && body) {
        blocks.push({ kind: "richParagraph", id: sid, body, bullets });
      } else if (bullets.length) {
        blocks.push({ kind: "richParagraph", id: sid, body: "", bullets });
      } else if (body) {
        blocks.push({ kind: "p", id: `${sid}-p`, text: body });
      }
      continue;
    }

    if (title) blocks.push({ kind: "h2", id: sid, text: title });
    if (body && bullets.length) {
      blocks.push({ kind: "richParagraph", id: sid, body, bullets });
    } else if (body) {
      blocks.push({ kind: "p", id: `${sid}-p`, text: body });
    } else if (bullets.length) {
      blocks.push({ kind: "richParagraph", id: sid, body: "", bullets });
    }
  }

  return blocks.length ? blocks : null;
};

const legacySegmentsToBlocks = (segments) =>
  segments.map((seg, i) => {
    if (seg.type === "ul") return { kind: "ul", id: `legacy-${i}`, items: seg.items };
    if (seg.type === "ol") return { kind: "ol", id: `legacy-${i}`, items: seg.items };
    return { kind: "p", id: `legacy-${i}`, text: seg.text };
  });

/** Insert space after period when the next character is a capital letter (e.g. "floors.Poor" → "floors. Poor"). */
const softBreakAfterPeriod = (text) => String(text || "").replace(/([.!?])([A-Z])/g, "$1 $2");

const BULLET_LEAD_RE = /^([^:\n]{1,56}?)\s*:\s*(.+)$/;

const BlogBulletLine = ({ text }) => {
  const normalized = softBreakAfterPeriod(String(text || ""));
  const m = normalized.match(BULLET_LEAD_RE);
  if (!m) return normalized;
  const label = m[1].trim();
  const rest = m[2];
  if (!label) return normalized;
  return (
    <>
      <strong className="blogDetails-listLead">{label}:</strong>
      {rest ? <> {rest}</> : null}
    </>
  );
};

const BlogDetails = () => {
  const { postId } = useParams();
  const location = useLocation();
  const { i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.language) === "rtl";
  const [posts, setPosts] = useState(fallbackDetailsPosts);
  const [startIndex, setStartIndex] = useState(0);
  const [copyFeedback, setCopyFeedback] = useState(null);
  const copyFeedbackTimerRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const loadPosts = async () => {
      try {
        const data = await getBlogPosts({ language: (i18n.language || "en").toLowerCase() });
        const list = Array.isArray(data) ? data : [];
        const mapped = list
          .map((item) => ({
            id: String(item.id),
            title: item.title || "",
            desc: item.text || "",
            text: item.text || "",
            date: formatDate(item.publishedAtUtc),
            image: resolveMediaUrl(item.imageUrl),
            caption: item.caption || "",
            sections:
              Array.isArray(item.sections)
                ? item.sections
                : Array.isArray(item.Sections)
                  ? item.Sections
                  : undefined,
          }))
          .filter((item) => item.id && (item.title || item.text || item.image));

        if (isMounted) {
          setPosts(mapped.length ? mapped : fallbackDetailsPosts);
        }
      } catch (_error) {
        if (isMounted) setPosts(fallbackDetailsPosts);
      }
    };
    loadPosts();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  useEffect(
    () => () => {
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
        copyFeedbackTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    setCopyFeedback(null);
    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = null;
    }
  }, [postId]);

  const post = posts.find((item) => String(item.id) === String(postId));
  const relatedPosts = useMemo(() => posts, [posts]);
  const bodyBlocks = useMemo(() => {
    if (!post) return [];
    const fromSections = buildBlocksFromSections(post.sections);
    if (fromSections?.length) return fromSections;
    return legacySegmentsToBlocks(mergeBlogBodyParagraphs(parseBlogBody(post.text)));
  }, [post]);

  const firstParagraphBlockIndex = useMemo(
    () =>
      bodyBlocks.findIndex(
        (b) =>
          (b.kind === "p" && b.text) || (b.kind === "richParagraph" && b.body && String(b.body).trim())
      ),
    [bodyBlocks]
  );
  const sharePageUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${location.pathname}${location.search}`;
  }, [location.pathname, location.search]);

  if (!post) return <Navigate to="/blog" replace />;

  const linkedinShareUrl = buildLinkedInShareUrl(sharePageUrl);
  const facebookShareUrl = buildFacebookShareUrl(sharePageUrl);
  const xShareUrl = buildXShareUrl(sharePageUrl, post.title);

  const visibleRelated = relatedPosts
    .slice(startIndex, startIndex + 3)
    .concat(relatedPosts.slice(0, Math.max(0, startIndex + 3 - relatedPosts.length)));

  const handlePrev = () => {
    setStartIndex((prev) => (prev - 1 + relatedPosts.length) % relatedPosts.length);
  };

  const handleNext = () => {
    setStartIndex((prev) => (prev + 1) % relatedPosts.length);
  };

  const showCopyFeedback = (kind) => {
    setCopyFeedback(kind);
    if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
    copyFeedbackTimerRef.current = setTimeout(() => {
      setCopyFeedback(null);
      copyFeedbackTimerRef.current = null;
    }, 2600);
  };

  const handleCopy = async () => {
    const url = typeof window !== "undefined" ? window.location.href : sharePageUrl;
    if (!url) {
      showCopyFeedback("error");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showCopyFeedback("success");
      return;
    } catch (_err) {
      // non-secure context or denied permission
    }
    try {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      showCopyFeedback("success");
    } catch (_err2) {
      showCopyFeedback("error");
    }
  };

  return (
    <div className="blogDetailsPage">
      <Navbar theme="dark" />

      <main className="blogDetails">
        <div className="blogDetails-container">
          <h1 className="blogDetails-title">{post.title}</h1>

          <p className="blogDetails-dateTop">
            <FiCalendar />
            <span>{post.date}</span>
          </p>

          <img src={post.image} alt={post.title} className="blogDetails-mainImage" />

          <div className="blogDetails-captionRow">
            <span>{post.caption}</span>
            <div className="blogDetails-share">
              <span>Share to</span>
              <a href={linkedinShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share to LinkedIn">
                <img src={linkedinIcon} alt="" />
              </a>
              <a href={facebookShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share to Facebook">
                <img src={facebookIcon} alt="" />
              </a>
              <a href={xShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on X">
                <img src={twitterIcon} alt="" />
              </a>
            </div>
          </div>

          <section className="blogDetails-body">
            {bodyBlocks.length > 0
              ? bodyBlocks.map((block, idx) => {
                  const key = `${post.id}-body-${block.id}-${idx}`;
                  if (block.kind === "h2") {
                    return (
                      <h2 key={key} className="blogDetails-contentHeading">
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.kind === "richParagraph") {
                    const isLede =
                      Boolean(block.body && String(block.body).trim()) && idx === firstParagraphBlockIndex;
                    return (
                      <div key={key} className="blogDetails-articleBlock">
                        {block.body ? (
                          <p
                            className={`blogDetails-paragraph${isLede ? " blogDetails-paragraph--lede" : ""}`}
                          >
                            {softBreakAfterPeriod(block.body)}
                          </p>
                        ) : null}
                        {block.bullets.length > 0 ? (
                          <ul className="blogDetails-list blogDetails-list--unordered">
                            {block.bullets.map((item, j) => (
                              <li key={`${key}-li-${j}`}>
                                <BlogBulletLine text={item} />
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    );
                  }
                  if (block.kind === "ul") {
                    return (
                      <ul key={key} className="blogDetails-list blogDetails-list--unordered">
                        {block.items.map((item, j) => (
                          <li key={`${key}-li-${j}`}>
                            <BlogBulletLine text={item} />
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  if (block.kind === "ol") {
                    return (
                      <ol key={key} className="blogDetails-list blogDetails-list--ordered">
                        {block.items.map((item, j) => (
                          <li key={`${key}-li-${j}`}>
                            <BlogBulletLine text={item} />
                          </li>
                        ))}
                      </ol>
                    );
                  }
                  const isLede = block.kind === "p" && idx === firstParagraphBlockIndex;
                  return (
                    <p
                      key={key}
                      className={`blogDetails-paragraph${isLede ? " blogDetails-paragraph--lede" : ""}`}
                    >
                      {softBreakAfterPeriod(block.text)}
                    </p>
                  );
                })
              : null}
          </section>

          <div className="blogDetails-bottomShare">
            <p>
              <FiCalendar />
              <span>{post.date}</span>
            </p>
            <div className="blogDetails-share">
              <span>Share to</span>
              <button type="button" onClick={handleCopy} aria-label="Copy article link">
                <img src={copyIcon} alt="" />
              </button>
              <a href={linkedinShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share to LinkedIn">
                <img src={linkedinIcon} alt="" />
              </a>
              <a href={facebookShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share to Facebook">
                <img src={facebookIcon} alt="" />
              </a>
              <a href={xShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on X">
                <img src={twitterIcon} alt="" />
              </a>
            </div>
          </div>

          <section className="blogDetails-related">
            <h2>Related posts</h2>
            <div className="blogDetails-relatedGrid">
              {visibleRelated.map((item) => (
                <article className="blogDetails-relatedCard" key={item.id}>
                  <img src={item.image} alt={item.title} />
                  <span>{item.date}</span>
                  <h3>{item.title}</h3>
                  <p className="blogDetails-relatedCardExcerpt">{item.desc}</p>
                  <Link to={`/blog/${item.id}`}>Read article</Link>
                </article>
              ))}
            </div>

            <div className="blogDetails-sliderControls">
              <button type="button" onClick={handlePrev} aria-label="Previous posts">
                <img src={isRtl ? rightArrow : leftArrow} alt="" />
              </button>
              <button type="button" onClick={handleNext} aria-label="Next posts">
                <img src={isRtl ? leftArrow : rightArrow} alt="" />
              </button>
            </div>
          </section>
        </div>
      </main>

      <Footer />

      {copyFeedback ? (
        <div
          className={`blogDetails-copyToast blogDetails-copyToast--${copyFeedback}`}
          role="status"
          aria-live="polite"
        >
          {copyFeedback === "success" ? "Link copied to clipboard" : "Could not copy link"}
        </div>
      ) : null}
    </div>
  );
};

export default BlogDetails;

